import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef, createElement } from 'react';
import PT from 'prop-types';
import styled from 'styled-components';
import { H as HelpTip } from '../HelpTip/HelpTip-NOgcj-z5.js';
import { f as formGroupRowStyleMixin } from '../FormGroup/formGroupRowStyleMixin-CfvP08OH.js';
import { f as formGroupColumnStyleMixin } from '../FormGroup/formGroupColumnStyleMixin-BwxOlcRD.js';
import { L as LabelLine } from '../FormGroup/LabelLine-BznJUjx_.js';
import { V as ValidationLine } from '../FormGroup/ValidationLine-DvbM7AIe.js';
import { P as Prefix } from './Prefix-DHRsfKx5.js';
import { S as Suffix } from './Suffix-sZsBLT5N.js';
import { I as InputGroup, a as InputValidationMessage, s as styleInputMixin } from './InputFormGroup.styles-Cni5ywII.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { i as isString } from '../LocalModules/storm-ui-utils/dist/esm/isString-D4CFRAVS-D4CFRAVS.js';
import { c as createPseudoUID } from '../LocalModules/storm-ui-utils/dist/esm/createPseudoUID-DIFqwkk_-Cdz2nMp1.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-DjxTmSal-DjxTmSal.js';
import { g as getFirstFocusableElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFirstFocusableElement-DegkMO1c-COahU-b6.js';
import { m as memoize } from '../LocalModules/storm-ui-utils/dist/esm/memoizeWeak-B497gDUy-B497gDUy.js';
import { T as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const renderInputValidationMessage = (value, messageId, message, messageE2EId, statusType) => /*#__PURE__*/React__default.createElement(ValidationLine, {
  role: "alert",
  "aria-live": "assertive",
  id: messageId
}, typeof message === 'function' ? message(value) : /*#__PURE__*/React__default.createElement(InputValidationMessage, {
  messageType: statusType,
  message: message,
  "data-e2e-id": messageE2EId
}));
const NodeSafeInputElement = typeof HTMLInputElement !== 'undefined' ? HTMLInputElement : Object;

/*
 * Creating styled components are expensive. Caching the results
 * so that we don't need to create new ones unnecessary.
 */
// eslint-disable-next-line max-len
const createStyledInput = memoize(inputElement => {
  const styledInput = /*#__PURE__*/styled(({
    /*
     * Exclude the props that are only passed to the style. If The props were passed to the
     * react node, they would be attached to the DOM node and throw a warning.
     */
    prefix,
    suffix,
    inputRef,
    ...inputProps
  }) => /*#__PURE__*/createElement(inputElement, {
    ...inputProps,
    // default html input vs custom inputElement
    ...(inputElement === 'input' ? {
      ref: inputRef
    } : {
      inputRef
    })
  }, inputProps.children)).withConfig({
    componentId: "sc-storm-ui-30074035__sc-1sj9x5i-0"
  })(["", ""], styleInputMixin);
  styledInput.displayName = 'Input';
  return styledInput;
});
const InputFormGroupPropTypes = {
  /**
   * he page unique identifier of the input.
   */
  id: PT.string.isRequired,
  /**
   * The class name passed to the wrapper div element.
   */
  className: PT.string,
  /**
   * Allows the `<input />` element to be replaced with a custom element.
   */
  inputElement: PT.any,
  /**
   * Denotes if the InputFormGroup should be disabled.
   */
  disabled: PT.bool,
  /**
   * The function called on input change.
   */
  onChange: PT.func,
  /**
   * Labels are required for screen reader accessibility, but can be hidden visually.
   */
  hideLabel: PT.bool,
  /**
   * Needed for accessibility when no other label is associated to the input.
   * See "Hiding labels" above to visually hide the label.
   */
  label: PT.node,
  /**
   * Used to render content *before* the label, but outside the click-able area.
   */
  renderLabelStart: PT.oneOfType([PT.func, PT.node]),
  /**
   * Used to render content *after* the label, but outside the click-able area.
   */
  renderLabelEnd: PT.oneOfType([PT.func, PT.node]),
  /**
   * Label and message will be inlined.
   */
  inline: PT.bool,
  /**
   * A placeholder value for the input area.
   */
  placeholder: PT.string,
  /**
   * Adds a message below the input, which will update based on error/success/warning.
   */
  message: PT.oneOfType([PT.string, PT.func]),
  /**
   * Specifies the type of message to be displayed (error, warning, success)
   */
  statusType: PT.oneOf(['error', 'warning', 'success']),
  /**
   * Use to match the width of the container.
   * Useful for setting specific widths (by setting width on container).
   */
  fullWidth: PT.bool,
  /**
   * Use a string of text or a React element (Such as <Icon type="search" />).
   */
  prefix: PT.node,
  /**
   * Use a string of text or a React element (Such as <Icon type="search" />).
   */
  suffix: PT.node,
  type: PT.string,
  /**
   * Use to access the <input> element ref.
   */
  inputRef: PT.oneOfType([PT.func, PT.shape({
    current: PT.instanceOf(NodeSafeInputElement)
  })]),
  /**
   * *Prop will be deprecated in a future version, use renderLabelEnd with `<HelpTip/>` instead.*
   * Adds an info tooltip with your help message (can pass a string or node).
   */
  help: PT.node,
  /**
   * A data-id that can be used for writing unit tests.
   */
  messageE2EId: PT.string,
  /**
   * The function called on input focus.
   */
  onFocus: PT.func,
  /**
   * The function called on input blur.
   */
  onBlur: PT.func,
  /**
   * Set the aria-label attribute on input.
   */
  ariaLabel: PT.string,
  /**
   * Ignores the HTML dir attribute when determining placement of prefix and suffix elements. This is useful for
   * displaying currencies where the placement of the symbol is the same regardless of writing direction.
   */
  ignoreTextDirection: PT.bool
};
const InputFormGroupDefaultProps = {
  inputElement: 'input',
  disabled: false,
  type: 'text',
  onChange: noop,
  label: undefined,
  hideLabel: false,
  renderLabelStart: undefined,
  renderLabelEnd: undefined,
  inline: false,
  placeholder: '',
  message: '',
  statusType: undefined,
  fullWidth: false,
  prefix: undefined,
  suffix: undefined,
  help: undefined,
  inputRef: undefined,
  messageE2EId: undefined,
  onFocus: undefined,
  onBlur: undefined,
  ariaLabel: undefined,
  className: '',
  ignoreTextDirection: undefined
};
class InputFormGroupComponent extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "inputGroup", /*#__PURE__*/createRef());
    _defineProperty(this, "styledInput", void 0);
    _defineProperty(this, "setEphemeralId", () => {
      const {
        ephemeralId
      } = this.state;
      // prevent changing the ephemeralId if one was already set
      if (!isString(ephemeralId)) {
        this.setState(() => ({
          ephemeralId: createPseudoUID()
        }));
      }
    });
    _defineProperty(this, "clearEphemeralId", () => {
      this.setState(() => ({
        ephemeralId: undefined
      }));
    });
    _defineProperty(this, "renderLabelEndWithHelptip", () => {
      const {
        renderLabelEnd,
        help
      } = this.props;
      return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, isFunction(renderLabelEnd) && renderLabelEnd(), /*#__PURE__*/React__default.createElement(HelpTip, {
        position: "right"
      }, help));
    });
    _defineProperty(this, "focusInput", () => {
      if (this.inputGroup.current) {
        const focusable = getFirstFocusableElement(this.inputGroup.current);
        if (focusable instanceof HTMLElement && isFunction(focusable.focus)) {
          focusable.focus();
        }
      }
    });
    this.state = {
      ephemeralId: undefined
    };
  }
  componentDidMount() {
    const {
      id
    } = this.props;

    /*
     * create a ephemeral message Id after mounted without a ID and the validation line has
     * been created
     */
    if (!isString(id)) {
      this.setEphemeralId();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      id
    } = this.props;

    /*
     * Deals with the edge case when a id prop is set or cleared when the validation line is
     * visible
     */
    if (id !== prevProps.id) {
      if (!isString(id)) {
        this.setEphemeralId();
      } else {
        this.clearEphemeralId();
      }
    }
  }
  render() {
    const {
      id,
      className,
      disabled,
      label,
      ariaLabel,
      message,
      messageE2EId,
      statusType,
      prefix,
      suffix,
      fullWidth,
      inline,
      hideLabel,
      inputRef,
      help,
      inputElement,
      renderLabelStart,
      renderLabelEnd,
      value,
      ignoreTextDirection,
      taktId,
      taktValue,
      ...rest
    } = this.props;
    const {
      ephemeralId
    } = this.state;
    this.styledInput = createStyledInput(inputElement);
    const labelId = isString(id) ? undefined : `${ephemeralId}-input-label`;
    const messageId = isString(id) ? `${id}-input-message` : `${ephemeralId}-input-message`;
    const a11yProps = {
      ...(ariaLabel ? {
        'aria-label': ariaLabel
      } : {}),
      /**
      * When custom aria-label or `id` prop is provided,
      * we do not need to do the labelledby work around
      */
      ...(!ariaLabel && isString(labelId) ? {
        'aria-labelledby': labelId
      } : {}),
      /**
      * Add a 'aria-describedby' when any message is applied to the input
      */
      ...(message ? {
        'aria-describedby': messageId
      } : {}),
      /**
      * Error get a `'aria-invalid="true"`
      */
      ...(statusType === 'error' ? {
        'aria-invalid': true
      } : {})
    };
    return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('input-form-group')
    }, ({
      getDataTaktAttributes
    }) => /*#__PURE__*/React__default.createElement("div", {
      className: className
    }, label && /*#__PURE__*/React__default.createElement(LabelLine, _extends({
      hidden: hideLabel,
      renderLabelStart: renderLabelStart,
      renderLabelEnd:
      /**
       * Support the `help` prop by folding it into renderLabelEnd
       */
      help ? this.renderLabelEndWithHelptip : renderLabelEnd
    }, isString(id) ? {
      labelFor: id
    } : {
      labelId,
      onLabelClick: this.focusInput
    }, getDataTaktAttributes({
      taktIdSuffix: 'label'
    })), label), /*#__PURE__*/React__default.createElement(InputGroup, {
      fullWidth: fullWidth,
      prefix: prefix,
      suffix: suffix,
      $statusType: statusType,
      inputGroupRef: this.inputGroup,
      dir: ignoreTextDirection ? 'ltr' : undefined,
      disabled: disabled
    }, prefix && /*#__PURE__*/React__default.createElement(Prefix, {
      $ignoreTextDirection: ignoreTextDirection,
      disabled: disabled
    }, prefix),
    /*#__PURE__*/
    /*
    * Using the non-JSX `React.createElement()` lets us choose between the named DOM
    * element and a function that returns a node, without adding additional flow control.
    */
    createElement(this.styledInput, {
      ...getDataTaktAttributes(),
      ...rest,
      ...(isString(id) ? {
        id
      } : {}),
      ...a11yProps,
      value,
      inputRef,
      disabled,
      prefix,
      suffix,
      $statusType: statusType,
      $ignoreTextDirection: ignoreTextDirection
    }, null /* children are null when assuming a closed tag like `<Input />` */), suffix && /*#__PURE__*/React__default.createElement(Suffix, {
      $ignoreTextDirection: ignoreTextDirection,
      disabled: disabled
    }, suffix)), !!message && renderInputValidationMessage(value, messageId, message, messageE2EId, statusType)));
  }
}
_defineProperty(InputFormGroupComponent, "displayName", 'InputFormGroupComponent');
_defineProperty(InputFormGroupComponent, "propTypes", InputFormGroupPropTypes);
_defineProperty(InputFormGroupComponent, "defaultProps", InputFormGroupDefaultProps);
const InputFormGroup = /*#__PURE__*/styled(InputFormGroupComponent).withConfig({
  componentId: "sc-storm-ui-30074035__sc-1sj9x5i-1"
})(["", " ", ""], ({
  inline
}) => inline ? formGroupRowStyleMixin : formGroupColumnStyleMixin, isMobile(formGroupColumnStyleMixin));
InputFormGroup.displayName = 'InputFormGroup';
InputFormGroup.propTypes = InputFormGroupPropTypes;
InputFormGroup.defaultProps = InputFormGroupDefaultProps;

export { InputFormGroup as I, renderInputValidationMessage as r };
//# sourceMappingURL=InputFormGroup-CFIeFrSM.js.map
